import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import * as moment from 'moment';

import {
  OrderBuilderActions,
  RESET_BUILDER,
} from '@ordersnproducts/modules/order-builder/actions/order-builder.actions';
import {
  ActionTypes,
  Actions,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/actions/open-platform/slots.actions';
import { SlotsResponseOP } from '@cemex-core/cmx-api';

export interface Slot {
  formIndex: number;
  loading?: boolean;
  response?: SlotsResponseOP;
  fetched?: boolean;
  availableDate?: moment.Moment | null;
  availableSchedule?: boolean;
  waitingListDefaulted?: boolean;
  error?: any;
  openResetWarning?: boolean;
  reset?: boolean;
}

export interface State extends EntityState<Slot> {
  selectedSlot: number | null;
}

export const adapter: EntityAdapter<Slot> = createEntityAdapter<Slot>({
  selectId: (_slot: Slot) => _slot.formIndex,
  sortComparer: false,
});

const initialState: State = adapter.getInitialState({
  selectedSlot: null,
});

export function reducer(state = initialState, action: Actions | OrderBuilderActions): State {
  switch (action.type) {
    case ActionTypes.ADD_SLOT: {
      return adapter.addOne(
        {
          formIndex: action.payload.formIndex,
          availableSchedule: action.payload.availableSchedule,
        },
        state,
      );
    }
    case ActionTypes.FETCH_SLOT: {
      return adapter.updateOne(
        {
          id: action.payload.formIndex,
          changes: {
            formIndex: action.payload.formIndex,
            response: null,
            fetched: false,
            loading: true,
            error: null,
            openResetWarning: false,
            reset: null,
          },
        },
        state,
      );
    }
    case ActionTypes.LOAD_SLOT: {
      return adapter.addOne(
        {
          formIndex: action.payload.formIndex,
          response: null,
          fetched: false,
          loading: true,
          error: null,
          openResetWarning: false,
          reset: null,
        },
        state,
      );
    }
    case ActionTypes.LOAD_SLOT_SUCCESS: {
      const apiResponse = action.payload.response;
      return adapter.updateOne(
        {
          id: action.payload.formIndex,
          changes: {
            availableSchedule: !!apiResponse.slots[0].concreteProposals.length,
            response: apiResponse,
            fetched: true,
            loading: false,
            error: null,
            openResetWarning: null,
            reset: null,
          },
        },
        state,
      );
    }
    case ActionTypes.ERROR: {
      return adapter.updateOne(
        {
          id: action.payload.formIndex,
          changes: {
            availableSchedule: false,
            fetched: true,
            error: action.payload.error,
            loading: false,
            openResetWarning: null,
            reset: null,
          },
        },
        state,
      );
    }
    case ActionTypes.SLOT_AVAILABLE: {
      return adapter.updateOne(
        {
          id: action.payload.formIndex,
          changes: {
            availableDate: action.payload.moment,
          },
        },
        state,
      );
    }
    case ActionTypes.SLOT_WAITING_LIST_DEFAULTED: {
      return adapter.updateOne(
        {
          id: action.payload.formIndex,
          changes: {
            waitingListDefaulted: action.payload.itDefaulted,
          },
        },
        state,
      );
    }
    case ActionTypes.TRIGGER_RESET_WARNING: {
      const slots: Slot[] = [];
      for (const key in state.entities) {
        if (Object.prototype.hasOwnProperty.call(state.entities, key)) {
          slots.push(state.entities[key]);
        }
      }
      const entityFound = slots.find(item => item.formIndex === action.payload);
      if (entityFound) {
        return adapter.updateOne(
          {
            id: action.payload,
            changes: {
              openResetWarning: true,
              reset: null,
            },
          },
          state,
        );
      } else {
        return adapter.addOne(
          {
            formIndex: action.payload,
            response: null,
            fetched: false,
            loading: true,
            error: null,
            openResetWarning: true,
            reset: null,
          },
          state,
        );
      }
    }
    case ActionTypes.RESET: {
      return adapter.updateOne(
        {
          id: action.payload,
          changes: {
            loading: null,
            response: null,
            fetched: null,
            availableDate: null,
            availableSchedule: null,
            waitingListDefaulted: null,
            error: null,
            openResetWarning: false,
            reset: true,
          },
        },
        state,
      );
    }
    case ActionTypes.CANCEL_RESET: {
      return adapter.updateOne(
        {
          id: action.payload,
          changes: {
            openResetWarning: false,
            reset: false,
          },
        },
        state,
      );
    }
    case ActionTypes.UPDATE_AVAILABLE_SCHEDULE_FLAG: {
      return adapter.updateOne(
        {
          id: action.payload.formIndex,
          changes: {
            availableSchedule: action.payload.value,
          },
        },
        state,
      );
    }
    case ActionTypes.CLEAN_SLOT: {
      return adapter.removeOne(action.payload.formIndex, state);
    }
    case ActionTypes.CLEAN_ALL_SLOTS:
    case RESET_BUILDER: {
      return adapter.removeAll(state);
    }
    default: {
      return state;
    }
  }
}
